import React from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Button } from './ui/button';
import { reportClientError } from '../lib/clientTelemetry';

const isChunkLoadError = (error) => {
  const value = `${error?.name || ''} ${error?.message || error || ''}`;
  return /ChunkLoadError|Loading (CSS )?chunk [^ ]+ failed|Failed to fetch dynamically imported module/i.test(value);
};

const recoverChunkLoad = async (error) => {
  if (!isChunkLoadError(error)) return false;
  const recoveryKey = `ar-chunk-recovery:${window.location.pathname}${window.location.search}`;
  const lastAttempt = Number(sessionStorage.getItem(recoveryKey) || 0);
  if (Date.now() - lastAttempt < 60_000) return false;
  sessionStorage.setItem(recoveryKey, String(Date.now()));
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    await registration?.update();
  } catch {
    // La actualización del service worker es auxiliar; la recarga sigue siendo segura.
  }
  window.location.reload();
  return true;
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunkError: false };
  }

  componentDidUpdate(previousProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, chunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AppErrorBoundary atrapó un error de render:', error, errorInfo);
    reportClientError(error, { source: 'react-boundary', componentStack: errorInfo?.componentStack });
    recoverChunkLoad(error);
  }

  handleRetry = () => {
    if (this.state.chunkError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, chunkError: false });
  };

  handleGoDashboard = () => {
    const currentPath = window.location.pathname || '';
    if (currentPath.startsWith('/vista-consejo')) {
      const condominiumId = new URLSearchParams(window.location.search).get('condominio_id');
      window.location.assign(`/vista-consejo${condominiumId ? `?condominio_id=${encodeURIComponent(condominiumId)}` : ''}`);
      return;
    }
    if (currentPath.startsWith('/consejo')) {
      window.location.assign('/consejo');
      return;
    }
    window.location.assign('/dashboard');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-lg w-full rounded-2xl border bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <AlertTriangle className="h-7 w-7 text-red-500" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">
            Ocurrió un error al cargar la sección
          </h1>
          <p className="text-slate-600 mb-6">
            {this.state.chunkError
              ? 'La aplicación se actualizó mientras esta sesión estaba abierta. Recarga para continuar con la versión vigente.'
              : 'Conservamos la navegación y cualquier texto pendiente. Intenta abrir nuevamente esta sección.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={this.handleRetry} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              {this.state.chunkError ? 'Actualizar y continuar' : 'Reintentar'}
            </Button>
            <Button variant="outline" onClick={this.handleGoDashboard}>
              Ir al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
